import { motion } from 'framer-motion'
import { RevealLines } from './Reveal'
import Seo from './Seo'
import { L, useLocale, useT } from '../i18n/locale.jsx'
import { getDepartments } from '../data/departments'
import { getSeo } from '../i18n/seo'
import { BOOKING_URL } from '../config'
import { calAttrs } from '../cal'

const EASE = [0.16, 1, 0.3, 1]

// Pagina-hub comună către cele 5 pagini de departament/industrie.
export default function Solutions() {
  const locale = useLocale()
  const t = useT().solutions
  const seo = getSeo(locale)
  const departments = getDepartments(locale)

  return (
    <>
      <Seo title={seo.solutions.title} description={seo.solutions.description} />

      {/* HERO — dark (forest via BgSwitch), transparent ca crossfade-ul global să treacă prin el */}
      <section data-theme="dark" className="relative flex min-h-screen items-center px-5 pt-32 text-cream md:px-10">
        <div className="mx-auto w-full max-w-site">
          <L
            to="/"
            className="mb-8 inline-flex min-h-[44px] items-center font-mono text-[12px] uppercase tracking-[0.1em] text-cream-muted transition-colors duration-300 ease-quint hover:text-honey"
          >
            {t.back}
          </L>
          <p className="mb-6 font-mono text-[12px] uppercase tracking-[0.1em] text-honey">{t.eyebrow} ✦</p>
          <h1 className="font-sans font-semibold tracking-tight max-w-[18ch] text-[11vw] leading-[1.05] md:text-[60px]">
            <RevealLines
              onMount
              lines={[t.h1, <>{t.h2pre}<em className="font-sans font-semibold not-italic text-honey">{t.h2em}</em>{t.h2post}</>]}
            />
          </h1>
          <p className="mt-7 max-w-[56ch] text-lg leading-relaxed text-cream-soft">{t.sub}</p>
        </div>
      </section>

      {/* Zonă-tampon verde — crossfade-ul global verde→alb cade pe spațiu gol (ca homepage). */}
      <div data-theme="dark" aria-hidden className="h-[78vh] md:h-[80vh]" />

      {/* LISTĂ DEPARTAMENTE — light (bone via BgSwitch) */}
      <section data-theme="light" className="px-5 py-24 text-ink md:px-10 md:py-32">
        <div className="mx-auto max-w-site border-t border-ink/15">
          {departments.map((d, i) => (
            <motion.div
              key={d.slug}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.55, ease: EASE, delay: (i % 2) * 0.05 }}
            >
              <L
                to={`/${d.slug}`}
                className="group grid gap-2 border-b border-ink/15 py-8 transition-colors duration-300 ease-quint hover:bg-ink/[0.03] md:grid-cols-[auto_1fr_auto] md:items-baseline md:gap-8"
              >
                <span className="font-mono text-xl leading-none text-ink/35 md:text-2xl">{String(i + 1).padStart(2, '0')}</span>
                <span>
                  <span className="font-sans font-semibold tracking-tight text-3xl leading-[1.1] text-ink md:text-[34px]">{d.label}</span>
                  <span className="mt-2 block max-w-[54ch] text-[15px] leading-relaxed text-ink-soft">{d.hero}</span>
                </span>
                <span
                  aria-hidden
                  className="font-mono text-xl text-ink transition-transform duration-300 ease-quint group-hover:translate-x-1.5 md:self-center"
                >
                  →
                </span>
              </L>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA — light */}
      <section data-theme="light" className="px-5 pb-32 text-ink md:px-10 md:pb-48">
        <div className="mx-auto max-w-3xl text-center">
          <p className="eyebrow mb-8">{t.ctaEyebrow} ✦</p>
          <h2 className="font-sans font-semibold tracking-tight text-4xl leading-[1.1] md:text-5xl">{t.ctaHeading}</h2>
          <p className="mx-auto mt-5 max-w-md text-lg text-ink-soft">{t.ctaSub}</p>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            {...calAttrs}
            className="mt-8 inline-flex min-h-[48px] items-center gap-2 rounded-full bg-ink px-6 py-3 font-mono text-xs uppercase tracking-[0.08em] text-cream transition-colors duration-300 ease-quint hover:bg-honey hover:text-ink"
          >
            {t.cta} <span aria-hidden>→</span>
          </a>
        </div>
      </section>
    </>
  )
}
